import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { TFunction, useTranslation } from 'react-i18next';
import {
  CustomAutocomplete,
  DateOnlySemanticDisplayFormat,
  DatePicker,
} from 'web-library';
import { useGetIExamMasterCode } from '../../../../api-refactored/user/useGetIExamMasterCode';
import { ExamCandidateDetails } from '../../../../api/candidate/exam/model';
import ControlledAutocomplete from '../../../../components/form-items/ControlledAutocomplete';
import ToggleDisplayEditFormItem from '../../../../components/ToggleDisplayEditFormItem';
import { fallbackDashString } from '../../../../utils/StringUtils';
import {
  getIExamDropdownItemLabel,
  getIExamMasterCodeSelected,
} from '../../../accountManagement/logic/useCreateCandidateForm';
import { convertPrStatusToDisplayName } from '../../../accountManagement/logic/usePrCitizenshipForm';
import { useStyles } from './style';
import {IExamMasterCodeItem} from "../../../../api/user/model";

/**
 * All the field in this component is disabled by default in edit state.
 * No requirement to allow the editing at the moment.
 *
 * @param examCandidateDetail candidate particular info
 * @param isEditState is editing state
 */

const NATIONALITY = (t: TFunction) => t('exam->>ReviewExam.Nationality');
const NATIONALITY_ISSUE_DATE = (t: TFunction) => t('exam->>ReviewExam.NationalityIssueDate');

const defaultDOBPickerProps = {
  placeholder: 'Enter date of birth',
  disableFuture: true,
  format: DateOnlySemanticDisplayFormat,
};
const NationalityDetails = ({
  examCandidateDetail,
  isEditState = false,
  isNonNational = false,
  isOpsAdmin = false,
  form,
}: {
  examCandidateDetail: ExamCandidateDetails;
  isEditState?: boolean;
  isNonNational?: boolean;
  isOpsAdmin?: boolean;
  form?: UseFormReturn<any>;
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const nationalityIssueDateString = examCandidateDetail?.nationalityIssueDate
    ? examCandidateDetail?.nationalityIssueDate
    : '-';

  // const [nationalityIssueDateDisplay, setNationalityIssueDateDisplay] = useState<string>(
  //   nationalityIssueDateString
  // );
  const { data: iExamsData } = useGetIExamMasterCode();

  return (
    <>
      {/** Display Nationality and Nationality Issue Date fields on top of Citizenship field in non-editable mode * */}
      {!isEditState && (
        <>
          <ToggleDisplayEditFormItem
            isEditState={isEditState}
            labelText={NATIONALITY(t)}
            valueText={examCandidateDetail?.nationality}
            containerClassName={`${classes.formElementContainer} ${classes.halfWidthLeftItem}`}
          />
          <ToggleDisplayEditFormItem
            isEditState={isEditState}
            allHide
            labelText={NATIONALITY_ISSUE_DATE(t)}
            valueText={nationalityIssueDateString}
            containerClassName={`${classes.formElementContainer} ${classes.halfWidthRightItem}`}
          />
        </>
      )}
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('exam->>ReviewExam.CitizenshipType')}
        valueText={examCandidateDetail?.citizenship}
        containerClassName={
          !isEditState
            ? `${classes.formElementContainer} ${classes.halfWidthLeftItem}`
            : `${classes.formElementContainer}`
        }
        EditComponent={
          <CustomAutocomplete
            label={t('exam->>ReviewExam.CitizenshipType')}
            placeholder="Select citizenship type"
            id="input-select-citizenship"
            value={examCandidateDetail?.citizenship}
            disabled
            options={[]}
          />
        }
      />
      {/** Display Nationality and Nationality Issue Date fields between Citizenship and PR Status fields in editable mode * */}
      {isEditState && (
        <>
          <ToggleDisplayEditFormItem
            isEditState={isEditState}
            labelText={NATIONALITY(t)}
            valueText={examCandidateDetail?.nationality}
            containerClassName={`${classes.formElementContainer}`}
            EditComponent={
              form && (
                <ControlledAutocomplete<IExamMasterCodeItem, false, false, undefined>
                  label={NATIONALITY(t)}
                  id="input-select-nationality"
                  control={form.control}
                  name="nationality"
                  disabled={!isOpsAdmin}
                  error={!!form.formState.errors?.nationality}
                  helperText={form.formState.errors?.nationality?.message?.toString()}
                  options={iExamsData ? iExamsData.nationality : []}
                  getOptionLabel={getIExamDropdownItemLabel}
                  isOptionEqualToValue={getIExamMasterCodeSelected}
                  isRequired
                />
              )
            }
          />
          <ToggleDisplayEditFormItem
            isEditState={isEditState}
            labelText={NATIONALITY_ISSUE_DATE(t)}
            valueText={nationalityIssueDateString}
            allHide
            containerClassName={`${classes.formElementContainer} ${classes.halfWidthRightItem}`}
            EditComponent={
              <DatePicker

                label={NATIONALITY_ISSUE_DATE(t)}
                onChange={(date) => {
                  form?.setValue('nationalityIssueDate', date);
                }}
                inputRef={(e)=>form?.register('nationalityIssueDate').ref(e)}
                name="nationalityIssueDate"
                inputVariant="filled"
                disabled={!isNonNational}
                {...defaultDOBPickerProps}
              />
            }
          />
        </>
      )}
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('exam->>ReviewExam.PrStatus')}
        valueText={convertPrStatusToDisplayName(fallbackDashString(examCandidateDetail?.prStatus))}
        containerClassName={`${classes.formElementContainer}`}
        EditComponent={
          <CustomAutocomplete
            label={t('exam->>ReviewExam.PrStatus')}
            id="input-select-prStatus"
            value={convertPrStatusToDisplayName(examCandidateDetail?.prStatus)}
            fullWidth
            disabled
            options={[]}
          />
        }
      />
    </>
  );
};
export default NationalityDetails;
